import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {Overlay} from 'components/customer/Product/QuickAdd/Overlay';
import {ItemType} from 'shared/components/Product/Item';
import {useProductContext} from 'contexts/ProductContext';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {PartialJob} from 'shared/types/PartialJob';
import {
    invalidateParticularRoomAndCost,
    useQuickAddProductMutation,
} from 'components/customer/Product/store/productApi';
import {useAppDispatch, store, useAppSelector} from 'store/customer';
import {genericMessageHandler} from 'shared/helpers';
import {invalidateTotalProductCount} from 'components/customer/Job/store/jobApi';
import {isMobile} from 'react-device-detect';
import {PartialRoom} from 'shared/types/PartialRoom';
import {quickAppendProductTo3dPreview} from 'components/customer/RoomPlanner/helpers/quickAppendProductTo3dPreview';
import {
    CabinetMesh,
    UndoActionContext,
} from 'components/customer/RoomPlanner/types';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {selectedMenuSet} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {dispatchCabinetAddToHistory} from 'components/customer/RoomPlanner/helpers/history/cabinetAddAction';
import {formatHistoryTitlePrefix} from 'components/customer/RoomPlanner/helpers/history/formatHistoryTitle';
import {useRoomPlannerLeftMenu} from 'components/customer/RoomPlanner/helper/useRoomPlannerLeftMenu';
import {
    addErrorPopover,
    addPopover,
    removeErrorPopover,
    removePopover,
    selectErrorPopovers,
    selectPopovers,
} from 'components/customer/Product/store/productSlice';
import styled from 'styled-components';

interface QuickAddProps {
    onClickHandler: () => void;
    item: ItemType;
    isMouseOver: boolean;
    onMouseLeave: () => void;
}

type SetState<T> = Dispatch<SetStateAction<T>>;

interface QuickAddInterface {
    onClickHandler?: () => void;
    item?: ItemType;
    quantity?: number;
    setQuantity?: SetState<number>;
    width?: number;
    setWidth?: SetState<number>;
    addProduct?: () => Promise<void>;
    loading?: boolean;
    startAnimation?: boolean;
    onAnimationComplete?: () => void;
    validationMessage?: string;
    setValidationMessage?: SetState<string>;
    canRetry?: boolean;
    isFocused?: boolean;
    onMouseLeave?: () => void;
    popoverOpen?: boolean;
    setPopoverOpen?: (isOpen: boolean) => void;
    isSuccess?: boolean;
    resetValidationMessage?: () => void;
}

export const QuickAddContext = React.createContext<QuickAddInterface>({});

export const useQuickAddContext = () => useContext(QuickAddContext);

export const BASE_CABINET = 1;
export const UPPER_CABINET = 3;

export const BASE_APPLIED_PANEL = 3;

const NON_L_SHAPED_CABINET = 0;
const L_KICKER_FRAME = 3;
const L_2_PART = 4;
const L_DRAWER_DOOR = 6;
const WAIT_TO_RECOVER = 3000;

export const getInitialWidth = (item: ItemType, room: PartialRoom) => {
    // 1 (LSHAPE), 2 (45_DEGREE) and 5 (3_TIER_PANTRY) are the only actual
    // l-shape cabinets. These other values seem to only exist to tag cabinets
    // as 'special' as-per the admin product form label ...
    if (
        item.lshape &&
        ![
            NON_L_SHAPED_CABINET,
            L_KICKER_FRAME,
            L_2_PART,
            L_DRAWER_DOOR,
        ].includes(item.lshape)
    ) {
        // this simply means cabinet is l-shaped and quick-add only
        // has one width field ...
        return 'leftWidth' in item ? (item.leftWidth ?? 1000) : 1000;
    }

    if (item.productType == BASE_APPLIED_PANEL) {
        if (item.style == BASE_CABINET) {
            return item.defaultDepth || Number(room.baseDepth) + 20;
        } else if (item.style == UPPER_CABINET) {
            return item.defaultDepth || Number(room.upperDepth) + 20;
        } else {
            return item.defaultDepth || Number(room.tallDepth) + 20;
        }
    }

    return item.defaultWidth || 500;
};

export const QuickAdd = ({
    item,
    onClickHandler,
    isMouseOver,
    onMouseLeave,
}: QuickAddProps) => {
    const dispatch = useAppDispatch();
    const {room, updateTotalProductCountManual} = useJobContext() as PartialJob;
    const {isMiniBrowser} = useProductContext();
    const errorPopovers = useAppSelector(selectErrorPopovers);
    const popovers = useAppSelector(selectPopovers);

    const keepOpen = errorPopovers.includes(item.id);
    const popoverOpen = popovers.includes(item.id);

    const messageToasted = useRef(false);

    const [quantity, setQuantity] = useState(1);
    const [width, setWidth] = useState(() => getInitialWidth(item, room));
    const [loading, setLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [canRetry, setCanRetry] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const {notify} = useNotificationContext();
    const [addProductManually] = useQuickAddProductMutation();
    const {cabinetGroupListRef} = useRoomPlannerContext();
    const {isRoomPlannerPage} = useRoomPlannerLeftMenu();

    const {setSelectedCabinetGroup} = useRoomPlannerContext();

    const resetValidationMessage = useCallback(() => {
        setValidationMessage('');
        setCanRetry(false);
    }, []);

    const setPopoverOpen = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                dispatch(addPopover(item.id));
            } else {
                dispatch(removePopover(item.id));
            }
        },
        [item]
    );

    const setSuccessWithTimeout = useCallback(() => {
        setPopoverOpen(false); // close popover
        setIsSuccess(true); // set state to true so button turns green
        setTimeout(() => {
            // wait specified time to turn the button back to normal
            setIsSuccess(false);
        }, WAIT_TO_RECOVER);
    }, []);

    const handleDeadlockAdd = useCallback(
        (
            errorData: {exception?: {previous?: {message?: string}}},
            ignoreDispatch: boolean,
            redoFn: (
                ignoreHistory?: boolean,
                ctx?: UndoActionContext
            ) => void | Promise<void>
        ): void => {
            const isDeadlock = (errorData.exception?.previous?.message ?? '')
                .toLowerCase()
                .includes('deadlock');

            if (!isDeadlock || !isMiniBrowser || ignoreDispatch) return;

            const {roomPlanner} = store.getState();
            dispatchCabinetAddToHistory(dispatch, {
                title: `**Add** ${formatHistoryTitlePrefix(roomPlanner.roomIndex, null)} ${item.name ?? ''} From Quick Add`.trimEnd(),
                data: {
                    id: NaN,
                    redoAction: (ctx) => void redoFn(true, ctx),
                },
            });
        },
        [isMiniBrowser, dispatch, item]
    );

    const addProduct = useCallback(
        async (
            ignoreHistoryDispatch = false,
            historyCtx?: UndoActionContext
        ) => {
            setLoading(true);
            setSelectedCabinetGroup?.(null);

            if (isNaN(width) || width <= 0) {
                setValidationMessage('Width must be greater than 0');
                setLoading(false);
                return;
            }

            if (item.minWidth && width < item.minWidth) {
                setValidationMessage(
                    `Width must be greater than or equal to ${item.minWidth}`
                );
                setLoading(false);
                return;
            }

            if (isNaN(quantity) || quantity <= 0) {
                setValidationMessage('Quantity must be greater than 0');
                setLoading(false);
                return;
            }

            let retryAfter: number | undefined;
            try {
                // The placeholder cuboids render while the product saves, so
                // they can land after a fast validation error. Wait for them
                // before cleaning up, removing only the ones this add made.
                let placeholders = Promise.resolve<CabinetMesh[]>([]);
                if (isMiniBrowser && cabinetGroupListRef) {
                    const existing = new Set(cabinetGroupListRef.current);
                    placeholders = quickAppendProductTo3dPreview(
                        item,
                        quantity,
                        width,
                        room,
                        cabinetGroupListRef
                    )
                        .then((): CabinetMesh[] =>
                            cabinetGroupListRef.current.filter(
                                (mesh) =>
                                    mesh.userData.isQuickAddPending &&
                                    !existing.has(mesh)
                            )
                        )
                        .catch((): CabinetMesh[] => []);
                }

                const result = await addProductManually({
                    cabinetType: item.id,
                    room: room.id,
                    width,
                    quantity,
                });

                if (result.error) {
                    if (!!cabinetGroupListRef) {
                        (await placeholders).forEach((mesh) => {
                            deleteCabinetMesh(mesh, false, cabinetGroupListRef);
                        });
                    }

                    if (
                        typeof result.error === 'object' &&
                        'data' in result.error
                    ) {
                        const {data} = result.error as {
                            data: {
                                messages?: string[];
                                error?: string;
                                retryAfter?: number;
                                exception?: {
                                    previous?: {message?: string};
                                };
                            };
                        };

                        if (data) {
                            if (
                                'messages' in data &&
                                Array.isArray(data.messages)
                            ) {
                                throw new Error(data.messages.join(', '));
                            }

                            if ('error' in data) {
                                if (
                                    'retryAfter' in data &&
                                    typeof data.retryAfter == 'number'
                                ) {
                                    retryAfter = data.retryAfter;
                                }

                                handleDeadlockAdd(
                                    data,
                                    ignoreHistoryDispatch,
                                    addProduct
                                );

                                throw new Error(data.error);
                            }
                        }
                    }

                    throw new Error(
                        'Could not save product, please try manually adding it from product editor.'
                    );
                }

                if (isMobile && isRoomPlannerPage) {
                    dispatch(selectedMenuSet(''));
                }

                updateTotalProductCountManual(false, true, quantity);
                dispatch(invalidateParticularRoomAndCost(room.id));
                dispatch(invalidateTotalProductCount());

                const cabinetId = result.data;
                const numericCabinetId =
                    cabinetId !== undefined ? Number(cabinetId) : NaN;

                // Always inform history (undo/redo) about the real created id, even on redo
                if (
                    historyCtx?.setRestoredCabinetId &&
                    !Number.isNaN(numericCabinetId)
                ) {
                    historyCtx.setRestoredCabinetId(numericCabinetId);
                }

                // But only push a new history entry on the initial add (not during redo)
                if (isMiniBrowser && !ignoreHistoryDispatch) {
                    const state = store.getState();
                    const roomIndex = state.roomPlanner.roomIndex;

                    // Pass null for roomCabNumber so the title shows "..." as a
                    // placeholder. The real cabinet number is patched in once
                    // renderCabinetGroup receives the server response and calls
                    // updateCabinetAddHistoryTitleRoomCabNumber — which avoids
                    // duplicate numbers when several cabinets are added at once.
                    dispatchCabinetAddToHistory(dispatch, {
                        title: `**Add** ${formatHistoryTitlePrefix(roomIndex, null)} ${item.name ?? ''} From Quick Add`.trimEnd(),
                        data: {
                            id: numericCabinetId,
                            redoAction: (ctx) => void addProduct(true, ctx),
                        },
                    });
                }
                setSuccessWithTimeout();
                genericMessageHandler(
                    notify,
                    {message: 'Product saved'},
                    'success'
                );
            } catch (e) {
                let errorMessage = 'An unknown error occurred';
                if (e instanceof Error) {
                    errorMessage = e.message;
                }
                setValidationMessage(errorMessage);

                if (typeof retryAfter != 'undefined') {
                    setCanRetry(true);
                    setTimeout(resetValidationMessage, retryAfter * 1000);
                }
            } finally {
                setLoading(false);
            }
        },
        [
            room,
            quantity,
            width,
            item,
            addProductManually,
            updateTotalProductCountManual,
            isMobile,
            isMiniBrowser,
            handleDeadlockAdd,
        ]
    );

    const handleAutoScroll = useCallback(() => {
        const productId = `#item-id-${item.id}`;
        const element = document.querySelector(productId);

        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [item]);

    useEffect(() => {
        if (validationMessage != '') {
            if (!messageToasted.current) {
                if (!popoverOpen) {
                    dispatch(addErrorPopover(item.id));
                    genericMessageHandler(
                        notify,
                        {
                            message: (
                                <ErrorContainer onClick={handleAutoScroll}>
                                    There was an error adding {item.name},{' '}
                                    please click here for more details
                                </ErrorContainer>
                            ),
                        },
                        'danger',
                        true,
                        10000
                    );
                    setPopoverOpen(true);
                    messageToasted.current = true;
                }
            }
        } else {
            messageToasted.current = false;
            dispatch(removeErrorPopover(item.id));
        }

        return () => {
            dispatch(removeErrorPopover(item.id));
        };
    }, [validationMessage, item]);

    useEffect(() => {
        if (isMouseOver && keepOpen) {
            dispatch(removeErrorPopover(item.id));
        }
    }, [isMouseOver, keepOpen]);

    return (
        <QuickAddContext.Provider
            value={{
                isSuccess,
                popoverOpen: popoverOpen || keepOpen,
                setPopoverOpen,
                onMouseLeave,
                canRetry,
                item,
                loading,
                onClickHandler,
                quantity,
                setQuantity,
                width,
                setWidth,
                addProduct,
                validationMessage,
                setValidationMessage,
                resetValidationMessage,
            }}>
            <div>
                <Overlay />
            </div>
        </QuickAddContext.Provider>
    );
};

const ErrorContainer = styled.div`
    cursor: pointer;
`;
